import { computed, type ComputedRef, type Ref } from 'vue'
import { getBuyFlowController } from '@/features/crosschain-buy/buy-flow/controller'
import {
  ACTIVE_BRIDGE_STATUSES,
  FlowStatus,
  isRecoverableError,
} from '@/features/crosschain-buy/domain/flow'
import type { FlowStep } from '@/features/crosschain-buy/domain/modal'
import { isFullyCompletedExecution } from '@/features/crosschain-buy/buy-flow/presentation/start-new-swap-guard'

export type RouteUxStatus =
  | 'empty'
  | 'invalid_amount'
  | 'invalidated'
  | 'loading'
  | 'ready_preview'
  | 'ready_executable'
  | 'success'
  | 'processing'
  | 'error_recoverable'
  | 'error_blocking'

// IDLE / WALLET_GATE / ERROR fall through to the 'wallet' default — they
// represent states before or outside an active route, so the wallet step is
// the right resting point.
const STATUS_TO_STEP: ReadonlyMap<string, FlowStep> = new Map([
  [FlowStatus.DESTINATION_SWAP_READY, 'swap'],
  [FlowStatus.DESTINATION_SWAPPING, 'swap'],
  [FlowStatus.SUCCESS, 'success'],
  [FlowStatus.QUOTE_LOADING, 'bridge'],
  [FlowStatus.QUOTE_READY, 'bridge'],
  [FlowStatus.BRIDGE_APPROVING, 'bridge'],
  [FlowStatus.BRIDGE_SWAPPING, 'bridge'],
  [FlowStatus.BRIDGE_TRACKING, 'bridge'],
  [FlowStatus.SOLANA_JUPITER_SWAPPING, 'bridge'],
  [FlowStatus.SOLANA_LAYERSWAP_BRIDGING, 'bridge'],
  [FlowStatus.INTERMEDIATE_SYMBIOSIS_APPROVING, 'bridge'],
  [FlowStatus.INTERMEDIATE_SYMBIOSIS_SWAPPING, 'bridge'],
  [FlowStatus.INTERMEDIATE_SYMBIOSIS_TRACKING, 'bridge'],
])

const statusToStep = (status: string): FlowStep => STATUS_TO_STEP.get(status) ?? 'wallet'

// A canonical route can always be retired from the foreground. Submitted or
// ambiguous transactions keep reconciling in the background.
const CANCELLABLE_STATUSES = new Set<string>([
  FlowStatus.BRIDGE_APPROVING,
  FlowStatus.BRIDGE_SWAPPING,
  FlowStatus.SOLANA_JUPITER_SWAPPING,
  FlowStatus.SOLANA_LAYERSWAP_BRIDGING,
])

// ACTIVE_BRIDGE_STATUSES lives in `services/crosschain/constants.ts` next
// to FlowStatus — keeping the active-set definition adjacent to the enum
// it gates makes flow-status additions surface every affected guard in
// one obvious place.

export interface UseRouteUxStatusDeps {
  selectedSourceTokenAddress: Ref<string | undefined>
  sourceAmount: Ref<string>
  hasQuotePrerequisites: () => boolean
}

export interface UseRouteUxStatusResult {
  currentStep: ComputedRef<FlowStep>
  routeUxStatus: ComputedRef<RouteUxStatus>
  canCancelExecution: ComputedRef<boolean>
  canAbandonExecution: ComputedRef<boolean>
  routeLocked: ComputedRef<boolean>
  networkSelectionLocked: ComputedRef<boolean>
  cancelExecution: () => Promise<boolean>
  endExecution: () => Promise<boolean>
}

export function useRouteUxStatus(deps: UseRouteUxStatusDeps): UseRouteUxStatusResult {
  const { selectedSourceTokenAddress, sourceAmount, hasQuotePrerequisites } = deps
  const controller = getBuyFlowController()

  const currentStep = computed<FlowStep>(() => statusToStep(controller.state.value.status))

  const canCancelExecution = computed(() => {
    const s = controller.state.value
    const hasSubmittedUnfinishedStep = s.steps.some(
      (step) => !!step.txHash && !['completed', 'failed'].includes(step.status)
    )
    return !hasSubmittedUnfinishedStep && CANCELLABLE_STATUSES.has(s.status)
  })

  const hasCommittedStep = computed(() =>
    controller.state.value.steps.some((step) => Boolean(step.txHash))
  )
  const hasDurableStepEvidence = computed(() =>
    controller.state.value.steps.some((step) => Boolean(step.txHash) || step.status !== 'pending')
  )
  const hasEvmCheckpoint = computed(() => {
    const state = controller.state.value
    return Boolean(state.evmApprovalCheckpoint ?? state.evmBridgeCheckpoint)
  })
  const executionOwned = computed(() => {
    const state = controller.state.value
    return (
      ACTIVE_BRIDGE_STATUSES.has(state.status) ||
      state.status === FlowStatus.SUCCESS ||
      hasCommittedStep.value ||
      hasEvmCheckpoint.value ||
      (Boolean(state.currentSwapId) &&
        (hasDurableStepEvidence.value || state.status === FlowStatus.ERROR))
    )
  })
  const canAbandonExecution = computed(
    () => executionOwned.value && !isFullyCompletedExecution(controller.state.value)
  )
  const routeLocked = computed(() => executionOwned.value)
  const networkSelectionLocked = computed(
    () => executionOwned.value && !isFullyCompletedExecution(controller.state.value)
  )
  const cancelExecution = controller.endExecution
  const endExecution = controller.endExecution

  const routeUxStatus = computed<RouteUxStatus>(() => {
    const s = controller.state.value
    const status = s.status
    const hasWallets = (s.evm.address || s.solana.address) && s.ton.address
    const hasSelection =
      selectedSourceTokenAddress.value && sourceAmount.value && Number(sourceAmount.value) > 0
    const hasQuote = !!s.quote
    const hasPreview = !!s.routePreview?.spottyAmount
    const isExecutable = hasPreview && !s.routePreview.isLoading

    if (status === FlowStatus.ERROR) {
      return isRecoverableError(s.lastError) ? 'error_recoverable' : 'error_blocking'
    }

    if (ACTIVE_BRIDGE_STATUSES.has(status)) {
      return 'processing'
    }

    if (status === FlowStatus.SUCCESS) {
      return 'success'
    }

    if (status === FlowStatus.QUOTE_LOADING) {
      return 'loading'
    }

    if (status === FlowStatus.QUOTE_READY) {
      return isExecutable ? 'ready_executable' : 'ready_preview'
    }

    if (status === FlowStatus.DESTINATION_SWAP_READY) {
      return 'ready_executable'
    }

    if (!hasWallets || !hasSelection) {
      return 'empty'
    }

    if (!hasQuote && !hasQuotePrerequisites()) {
      return 'invalid_amount'
    }

    if (!hasQuote) {
      return 'invalidated'
    }

    return 'empty'
  })

  return {
    currentStep,
    routeUxStatus,
    canCancelExecution,
    canAbandonExecution,
    routeLocked,
    networkSelectionLocked,
    cancelExecution,
    endExecution,
  }
}
